import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Chip, Avatar } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import apiService from "../../../../service/Api";

interface PaymentStats {
  pending: number;
  paid: number;
  partially_paid: number;
  other: number;
  sponsored: number;
  cancelled: number;
  total: number;
}

interface PaymentStatusChartItem {
  id: number;
  value: number;
  label: string;
  color: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PaymentStatusWidget: React.FC = () => {
  const [stats, setStats] = useState<PaymentStats | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.get<PaymentStats>(
        `/api/bookings/booking/payment-statistics/`,
      );
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch payment statistics:", error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const primaryColor = "#4285f4";

  const paymentStatusData: PaymentStatusChartItem[] = stats
    ? [
        {
          id: 0,
          label: "Pending",
          value: stats.pending,
          color: "#F79E1B",
        },
        {
          id: 1,
          label: "Paid",
          value: stats.paid,
          color: "#4BA663",
        },
        {
          id: 2,
          label: "Sponsored",
          value: stats.sponsored,
          color: "#4285f4",
        },
        {
          id: 3,
          label: "Other",
          value: stats.other,
          color: "#EB4335",
        },

        {
          id: 4,
          label: "Cancelled",
          value: stats.cancelled,
          color: "#616161",
        },
      ].filter((item) => item.value > 0)
    : [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <PieChart
        series={[
          {
            data: paymentStatusData,
            innerRadius: 50,
            outerRadius: 90,
            paddingAngle: 2,
            cornerRadius: 4,
            highlightScope: { fade: "global", highlight: "item" },
            faded: {
              innerRadius: 20,
              additionalRadius: -20,
              color: "gray",
            },
          },
        ]}
        height={200}
        margin={{ top: 15, bottom: 15, left: 15, right: 15 }}
      />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "center",
        }}
      >
        {paymentStatusData.map((status, idx) => (
          <Chip
            key={idx}
            avatar={
              <Avatar
                sx={{
                  bgcolor: status.color || primaryColor,
                  width: 16,
                  height: 16,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    bgcolor: "background.paper",
                    borderRadius: "50%",
                  }}
                />
              </Avatar>
            }
            label={`${status.label}: ${status.value}`}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: `${status.color}15`,
              border: "1px solid",
              borderColor: `${status.color}30`,
              fontSize: "0.7rem",
            }}
          />
        ))}
      </Box>

      {stats && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 600, fontSize: "0.7rem" }}
        >
          Total: {stats.total.toLocaleString()} payments
        </Typography>
      )}
    </Box>
  );
};

export default PaymentStatusWidget;
